import { LitElement, html, TemplateResult, css, unsafeCSS, type PropertyValues } from 'lit';
import { property, state } from 'lit/decorators.js';
import { Task } from '@lit/task';
import { ifDefined } from 'lit/directives/if-defined.js';
import { localized } from '@lit/localize';
import mainStyles from './styles/main.scss?inline';
import { updateLocale } from './localization/config';
import { LocaleCode } from './types/lang.types';
import { RoundtripOfferResponse, RoundtripOfferViewModel } from './types/offer.types';
import { interceptChildEvents, setInitialUri, triggerSchemaJsonEvent } from './services/schema-org-json-ld.service';
import './components/price-box/index.js';
import './components/calendar/index.js';

enum Variant {
    DEFAULT = 'default',
    FIXED = 'fixed',
    CALENDAR = 'calendar',
}

type ContentState = 'loading' | 'loaded' | 'empty';

@localized()
export class RoundtripOfferPanel extends LitElement {
    static override styles = [
        css`
            ${unsafeCSS(mainStyles)}
        `,
    ];
    @property({ type: String }) public lang = '';
    @property({ type: String }) public currency = '';
    @property({ type: String }) public brand = '';
    @property({ type: String }) public tenant = '';
    @property({ type: String, attribute: 'identifier' }) identifier: string = '';
    @property({ type: Object, attribute: 'additional-tracking-data' }) public additionalTrackingData = {};
    @property({ type: String, attribute: 'variant' }) variant: Variant = Variant.DEFAULT;
    @property({ type: String, attribute: 'mode' }) mode: 'sticky' | 'default' = 'default';
    @property({ type: String, attribute: 'analytics-name' }) analyticsName: string = '';
    @property({ type: Number, attribute: 'booking-day-offset' }) bookingDayOffset = 3;
    @property({ type: String, attribute: 'fallback-price' }) fallbackPrice: string = '';
    @property({ type: String, attribute: 'duration-string' }) durationString: string = '';
    @property({ type: String, attribute: 'component-title' }) componentTitle: string = '';
    @property({ type: String, attribute: 'phone-number' }) phoneNumber: string = '';
    @property({ type: String, attribute: 'roundtrip-id' }) roundtripId: string = '';
    @property({ type: String, attribute: 'roundtrip-key' }) roundtripKey: string = '';
    @property({ type: String, attribute: 'roundtrip-types' }) roundtripTypes: string = '';
    @property({ type: String, attribute: 'start-end-destination' }) startDestination: string = '';
    @property({ type: String, attribute: 'start-point' }) startPoint: string = '';
    @property({ type: String, attribute: 'languages' }) languages: string = '';
    @property({ type: String, attribute: 'group-size' }) groupSize: string = '';
    @property({ type: String, attribute: 'best-travel-time' }) bestTravelTime: string = '';
    @property({ type: Boolean, attribute: 'show-operator' }) showOperator: boolean = false;
    @property({ type: String, attribute: 'operator-name' }) operatorName: string = '';
    @property({ type: Boolean, attribute: 'active' }) active = true;
    @property({ type: String, attribute: 'facet_audience' }) facetAudience = '';
    @property({ type: String, attribute: 'facet_style' }) facetStyle = '';
    @property({ type: String, attribute: 'facet_motive' }) facetMotive = '';

    @state() offers?: RoundtripOfferViewModel[] = [];
    @state() private selectedMonth: string = new Date().toISOString().substring(0, 7);
    @state() roundtripOfferResponse?: RoundtripOfferResponse;

    private _currentRequestController?: AbortController;
    private _currentBrand: string | null = null;
    private _offerFetchTimeoutId: number | null = null;
    private _visibilityRetryTimeoutId: number | null = null;
    private _removeSchemaEventInterceptor: (() => void) | null = null;

    private _getEffectiveCurrency(): string {
        const normalized = (this.currency ?? '').trim().toUpperCase();
        return /^[A-Z]{3}$/.test(normalized) ? normalized : 'CHF';
    }

    /**
     * Derives the graceful-degradation state purely from `fallbackPrice` -
     * the same value RoundtripStage populates from its own middleware fetch.
     * As long as it's empty, the panel shows the skeleton; the moment a
     * price arrives, the real panel renders.
     */
    private get _skeletonState(): ContentState {
        if (!this.fallbackPrice?.trim()) {
            return 'loading';
        }

        return 'loaded';
    }

    async connectedCallback(): Promise<void> {
        super.connectedCallback();
        const langAttribute = this.lang ?? 'de-CH';
        await updateLocale(langAttribute);

        this._currentBrand = this.brand || null;
        this._removeSchemaEventInterceptor?.();
        this._removeSchemaEventInterceptor = interceptChildEvents(this);
    }

    override firstUpdated(changedProperties: PropertyValues): void {
        super.firstUpdated(changedProperties);
        this._scheduleOffersFetch();
    }

    updated(changedProperties: Map<string, unknown>): void {
        super.updated(changedProperties);

        const requestRelevantKeys: Array<keyof RoundtripOfferPanel> = [
            'identifier',
            'lang',
            'brand',
            'tenant',
            'currency',
            'roundtripId',
            'roundtripKey',
            'active',
        ];

        // Handle language changes at runtime
        if (changedProperties.has('lang')) {
            const newLang = this.lang ?? 'de';
            console.log(`Language changed to: ${newLang}`);
            updateLocale(newLang).then(() => {
                this.requestUpdate();
            });
        }
        // Handle brand changes at runtime
        if (changedProperties.has('brand') && this.brand !== this._currentBrand) {
            console.log(`🔄 Brand changed from "${this._currentBrand}" to "${this.brand}"`);
            // Update data-brand attribute
            this._currentBrand = this.brand;
        }

        if (changedProperties.has('variant') && this.variant === Variant.CALENDAR) {
            this._activateCalendarData();
        }

        if (changedProperties.has('selectedMonth') && this.variant === Variant.CALENDAR) {
            this._scheduleOffersFetch();
        }

        if (requestRelevantKeys.some((key) => changedProperties.has(key))) {
            this._scheduleOffersFetch();
        }
    }

    private _emitSchemaJsonEvent(offer: RoundtripOfferViewModel): void {
        triggerSchemaJsonEvent(
            {
                uri: setInitialUri(this.roundtripId),
                data: offer,
            },
            this
        );
    }

    private _activateCalendarData() {
        this._scheduleOffersFetch();
    }

    private _isVisibleForFetch(): boolean {
        const style = window.getComputedStyle(this);
        if (style.display === 'none' || style.visibility === 'hidden') {
            return false;
        }

        const dialogEl = this.closest('dialog');
        if (dialogEl && !dialogEl.open) {
            return false;
        }

        const stickyContainer = this.closest('.der-tm-roundtrip-stage__sticky-bar');
        if (stickyContainer && !stickyContainer.classList.contains('is-visible') && !this.active) {
            return false;
        }

        // Size checks are unreliable during Storybook's initial layout phase.
        return true;
    }

    private _scheduleOffersFetch(): void {
        if (!this._isVisibleForFetch()) {
            if (this._visibilityRetryTimeoutId == null) {
                this._visibilityRetryTimeoutId = window.setTimeout(() => {
                    this._visibilityRetryTimeoutId = null;
                    this._scheduleOffersFetch();
                }, 150);
            }
            return;
        }

        if (this._offerFetchTimeoutId != null) {
            window.clearTimeout(this._offerFetchTimeoutId);
        }

        this._offerFetchTimeoutId = window.setTimeout(() => {
            this._offerFetchTimeoutId = null;
            void this._getRoundtripOffers.run();
        }, 0);
    }

    private _getRoundtripOffers: Task = new Task(this, {
        autoRun: false,
        task: async () => {
            // Cancel any previous request
            if (this._currentRequestController) {
                this._currentRequestController.abort();
            }

            // Create new abort controller for this request.
            const requestController = new AbortController();
            this._currentRequestController = requestController;

            this.offers = [];

            const responseData: RoundtripOfferResponse = {
                offers: [],
                nextOffer: null,
                bestPrice: null,
            };

            // Only update state if this request is still the current one
            if (!requestController.signal.aborted) {
                this.roundtripOfferResponse = responseData;
                const fetchedOffers = Array.isArray(responseData.offers) ? responseData.offers : [];
                this.offers = fetchedOffers;
                this.requestUpdate();

                const offerForSchemaEvent =
                    fetchedOffers.find((offer) => offer?.price?.bestPrice === true) ?? fetchedOffers[0];

                if (offerForSchemaEvent) {
                    this._emitSchemaJsonEvent(offerForSchemaEvent);
                }
            }
        },
        args: () =>
            [
                this.identifier,
                this.lang,
                this.brand,
                this.tenant,
                this._getEffectiveCurrency(),
                this.roundtripId,
                this.roundtripKey,
                this.selectedMonth,
            ] as [string, LocaleCode, string, string, string, string, string, string],
    });

    private _handleCalendarMonthChange(event: CustomEvent<{ month: string }>): void {
        event.stopPropagation();
        const month = event.detail?.month;
        if (!month || month === this.selectedMonth) {
            return;
        }
        this.selectedMonth = month;
    }

    private _handlePriceBoxClickAction(event: CustomEvent): void {
        event.stopPropagation();
        event.preventDefault();

        // Ensure at least one offer request was made before evaluating hasOffers.
        if (!this.roundtripOfferResponse) {
            this._scheduleOffersFetch();
        }

        const hasOffers =
            (Array.isArray(this.offers) && this.offers.length > 0) || this.roundtripOfferResponse?.nextOffer != null;
        const detail = {
            hasOffers,
            offersCount: this.offers?.length ?? 0,
        };

        if (this.mode === 'sticky') {
            this.variant = Variant.CALENDAR;
        }
        this.dispatchEvent(
            new CustomEvent('der-tm-offer-panel-price-box-action', {
                detail,
                bubbles: true,
                composed: true,
            })
        );
    }

    override disconnectedCallback(): void {
        super.disconnectedCallback();
        if (this._offerFetchTimeoutId != null) {
            window.clearTimeout(this._offerFetchTimeoutId);
            this._offerFetchTimeoutId = null;
        }
        if (this._visibilityRetryTimeoutId != null) {
            window.clearTimeout(this._visibilityRetryTimeoutId);
            this._visibilityRetryTimeoutId = null;
        }
    }

    _getFormattedPrice(price: number | null | undefined) {
        return price;
    }

    /**
     * Price-box loading placeholder. Deliberately self-contained (own
     * classes, hardcoded fallback colors) instead of reusing the real
     * .m-price-box__* classes / <der-ui-button>
     */
    private _renderPriceBoxSkeleton(): TemplateResult {
        if (this.variant === Variant.FIXED) {
            return html`
                <div
                    class="der-tm-offer-panel-standalone-skeleton der-tm-offer-panel-standalone-skeleton--fixed"
                    aria-hidden="true"
                >
                    <div class="der-tm-offer-panel-standalone-skeleton__price-row">
                        <div
                            class="der-tm-offer-panel-standalone-skeleton__amount der-tm-offer-panel-skeleton-fill"
                        ></div>
                        <div
                            class="der-tm-offer-panel-standalone-skeleton__per-person der-tm-offer-panel-skeleton-fill"
                        ></div>
                    </div>
                    <div class="der-tm-offer-panel-standalone-skeleton__button der-tm-offer-panel-skeleton-fill"></div>
                </div>
            `;
        }

        // Same field truthiness + order as renderRoundtripData() in
        // price-box-default.template.ts.
        const roundtripDataFieldCount = [
            this.roundtripTypes,
            this.groupSize,
            this.startDestination,
            this.startPoint,
            this.durationString,
            this.languages,
            this.bestTravelTime,
        ].filter((value) => Boolean(value)).length;

        return html`
            <div class="der-tm-offer-panel-standalone-skeleton" aria-hidden="true">
                ${this.componentTitle
                    ? html`<div
                          class="der-tm-offer-panel-standalone-skeleton__title der-tm-offer-panel-skeleton-fill"
                      ></div>`
                    : ''}
                <div class="der-tm-offer-panel-standalone-skeleton__buttons">
                    <div class="der-tm-offer-panel-standalone-skeleton__button der-tm-offer-panel-skeleton-fill"></div>
                    ${this.phoneNumber
                        ? html`<div
                              class="der-tm-offer-panel-standalone-skeleton__button der-tm-offer-panel-skeleton-fill"
                          ></div>`
                        : ''}
                </div>
                <div class="der-tm-offer-panel-standalone-skeleton__price-block">
                    <div class="der-tm-offer-panel-standalone-skeleton__price-row">
                        <div
                            class="der-tm-offer-panel-standalone-skeleton__amount der-tm-offer-panel-skeleton-fill"
                        ></div>
                        <div
                            class="der-tm-offer-panel-standalone-skeleton__per-person der-tm-offer-panel-skeleton-fill"
                        ></div>
                    </div>
                    <div class="der-tm-offer-panel-standalone-skeleton__data-rows">
                        ${Array.from({ length: roundtripDataFieldCount }).map(
                            () => html`
                                <div class="der-tm-offer-panel-standalone-skeleton__data-row">
                                    <div
                                        class="der-tm-offer-panel-standalone-skeleton__data-icon der-tm-offer-panel-skeleton-fill"
                                    ></div>
                                    <div class="der-tm-offer-panel-standalone-skeleton__data-text">
                                        <div
                                            class="der-tm-offer-panel-standalone-skeleton__data-label der-tm-offer-panel-skeleton-fill"
                                        ></div>
                                        <div
                                            class="der-tm-offer-panel-standalone-skeleton__data-value der-tm-offer-panel-skeleton-fill"
                                        ></div>
                                    </div>
                                </div>
                            `
                        )}
                    </div>
                </div>
            </div>
        `;
    }

    /**
     * Calendar loading placeholder. Same self-contained approach as the
     * price-box skeleton above - own classes, no dependency on
     *  so it never
     * shows an unstyled flash regardless of UI-library load timing. The real
     * calendar is only ever rendered once data has settled (see render()).
     *
     */
    private _renderCalendarSkeleton(): TemplateResult {
        const weekdayPlaceholders = [0, 1, 2, 3, 4, 5, 6];
        const dayPlaceholders = Array.from({ length: 42 });

        return html`
            <div class="der-tm-offer-panel-standalone-calendar-skeleton" aria-hidden="true">
                <div
                    class="der-tm-offer-panel-standalone-calendar-skeleton__header der-tm-offer-panel-skeleton-fill"
                ></div>
                <div class="der-tm-offer-panel-standalone-calendar-skeleton__month-row">
                    <div
                        class="der-tm-offer-panel-standalone-calendar-skeleton__nav der-tm-offer-panel-skeleton-fill"
                    ></div>
                    <div
                        class="der-tm-offer-panel-standalone-calendar-skeleton__month-title der-tm-offer-panel-skeleton-fill"
                    ></div>
                    <div
                        class="der-tm-offer-panel-standalone-calendar-skeleton__nav der-tm-offer-panel-skeleton-fill"
                    ></div>
                </div>
                <div class="der-tm-offer-panel-standalone-calendar-skeleton__weekdays">
                    ${weekdayPlaceholders.map(
                        () => html`
                            <div
                                class="der-tm-offer-panel-standalone-calendar-skeleton__weekday der-tm-offer-panel-skeleton-fill"
                            ></div>
                        `
                    )}
                </div>
                <div class="der-tm-offer-panel-standalone-calendar-skeleton__days">
                    ${dayPlaceholders.map(
                        () => html`
                            <div class="der-tm-offer-panel-standalone-calendar-skeleton__day">
                                <div
                                    class="der-tm-offer-panel-standalone-calendar-skeleton__day-number der-tm-offer-panel-skeleton-fill"
                                ></div>
                                <div
                                    class="der-tm-offer-panel-standalone-calendar-skeleton__day-price der-tm-offer-panel-skeleton-fill"
                                ></div>
                            </div>
                        `
                    )}
                </div>
                <div
                    class="der-tm-offer-panel-standalone-calendar-skeleton__button-primary der-tm-offer-panel-skeleton-fill"
                >
                    <div class="der-tm-offer-panel-standalone-calendar-skeleton__button-primary-line"></div>
                    <div class="der-tm-offer-panel-standalone-calendar-skeleton__button-primary-line"></div>
                </div>
                <div
                    class="der-tm-offer-panel-standalone-calendar-skeleton__button der-tm-offer-panel-skeleton-fill"
                ></div>
                <div
                    class="der-tm-offer-panel-standalone-calendar-skeleton__contact der-tm-offer-panel-skeleton-fill"
                ></div>
            </div>
        `;
    }

    protected render(): TemplateResult | undefined {
        const effectiveCurrency = this._getEffectiveCurrency();
        const skeletonState = this._skeletonState;

        if (this.variant !== Variant.CALENDAR) {
            if (skeletonState === 'loading') {
                return html`
                    <div class="der-tm-roundtrip-offer-panel-skeleton-region loading">
                        ${this._renderPriceBoxSkeleton()}
                    </div>
                `;
            }

            if (skeletonState === 'empty') {
                return html`<div class="der-tm-roundtrip-offer-panel-skeleton-region empty"></div>`;
            }

            const formattedPrice = this._getFormattedPrice(this.roundtripOfferResponse?.bestPrice);
            const price = formattedPrice ?? this.fallbackPrice;

            return html`
                <div class="der-tm-roundtrip-offer-panel-skeleton-region loaded">
                    <der-tm-roundtrip-offer-panel-price-box
                        lang=${this.lang}
                        brand=${this.brand}
                        currency=${effectiveCurrency}
                        tenant=${this.tenant}
                        analytics-name=${this.analyticsName}
                        phone-number=${this.phoneNumber}
                        component-title=${this.componentTitle}
                        price=${ifDefined(price != null ? String(price) : undefined)}
                        duration-string=${this.durationString}
                        start-end-destination=${this.startDestination}
                        start-point=${this.startPoint}
                        languages=${this.languages}
                        group-size=${this.groupSize}
                        best-travel-time=${this.bestTravelTime}
                        roundtrip-types=${this.roundtripTypes}
                        roundtrip-id=${this.roundtripId}
                        roundtrip-key=${this.roundtripKey}
                        ?show-operator=${this.showOperator}
                        ?fixed-mode=${this.variant === Variant.FIXED}
                        @der-price-box-click=${this._handlePriceBoxClickAction}
                    ></der-tm-roundtrip-offer-panel-price-box>
                </div>
            `;
        }

        if (skeletonState === 'loading') {
            return html`
                <div class="der-tm-roundtrip-offer-panel-skeleton-region loading">
                    ${this._renderCalendarSkeleton()}
                </div>
            `;
        }

        if (skeletonState === 'empty') {
            return html`<div class="der-tm-roundtrip-offer-panel-skeleton-region empty"></div>`;
        }

        return html`
            <div class="der-tm-roundtrip-offer-panel-skeleton-region loaded">
                <der-tm-roundtrip-offer-panel-calendar-default
                    lang=${this.lang}
                    currency=${effectiveCurrency}
                    brand=${this.brand}
                    tenant=${this.tenant}
                    analytics-name=${this.analyticsName}
                    phone-number=${this.phoneNumber}
                    start-end-destination=${this.startDestination}
                    start-point=${this.startPoint}
                    languages=${this.languages}
                    best-travel-time=${this.bestTravelTime}
                    roundtrip-types=${this.roundtripTypes}
                    roundtrip-id=${this.roundtripId}
                    roundtrip-key=${this.roundtripKey}
                    booking-day-offset=${this.bookingDayOffset}
                    .selectedMonth=${this.selectedMonth}
                    .roundtripOfferResponse=${this.roundtripOfferResponse ?? {
                        offers: [],
                        nextOffer: null,
                        bestPrice: null,
                    }}
                    @der-tm-calendar-month-change=${this._handleCalendarMonthChange}
                ></der-tm-roundtrip-offer-panel-calendar-default>
            </div>
        `;
    }
}

if (!customElements.get('der-tm-roundtrip-offer-panel')) {
    customElements.define('der-tm-roundtrip-offer-panel', RoundtripOfferPanel);
}
